
import { useMemo, useState } from 'react'
import { Button, IconButton } from '../ui-kitNew/buttonNew'
import { Popover } from '../ui-kitNew/popoverNew'
import { Select } from '../ui-kitNew/selectNew'
import { MultiSelectList } from '../ui-kitNew/multi-selectNew'
import { Segmented } from '../ui-kitNew/controlsNew'
import { cn } from '../ui-kitNew/cnNew'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  FilterIcon,
  XIcon,
} from '../ui-kitNew/iconsNew'
import { getField } from '../../lib/reportNew/fieldsNew'
import { formatFieldValue } from '../../lib/reportNew/formatNew'
import { isConditionActive, operatorMeta, operatorsForField } from '../../lib/reportNew/filtersNew'
import { ValueInput } from './value-inputNew'

const FACET_ID = (key) => `col-${key}-in`
const CUSTOM_ID = (key) => `col-${key}-custom`

/** Valores distintos del campo con su nº de registros, sobre los datos ya filtrados. */
function useFacets(rows, fieldKey) {
  return useMemo(() => {
    const counts = new Map()
    rows.forEach((row) => {
      const raw = row[fieldKey]
      const key = raw === null || raw === undefined || raw === '' ? '' : raw
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({
        value: typeof value === 'boolean' ? String(value) : value,
        label: value === '' ? 'Sin valor' : formatFieldValue(fieldKey, value),
        hint: `${count.toLocaleString('es-ES')} registros`,
      }))
  }, [rows, fieldKey])
}

/**
 * Menú de una columna de la tabla: ordenación, ocultar y filtrado dinámico.
 * El contenido depende del tipo de la columna: lista de valores con recuento
 * para textos y enumerados, rangos para números y fechas, y una condición
 * adicional con todos los operadores del tipo.
 */
export function ColumnFilterMenu({
  fieldKey,
  rows,
  facetRows,
  group,
  onChange,
  sort,
  onSort,
  onHide,
  canHide = true,
}) {
  const field = getField(fieldKey)
  const facets = useFacets(facetRows || rows, fieldKey)
  const conditions = group?.conditions || []

  const facetCondition = conditions.find((item) => item.id === FACET_ID(fieldKey))
  const customCondition = conditions.find((item) => item.id === CUSTOM_ID(fieldKey))
  const activeCount = conditions.filter(isConditionActive).length

  const showFacets = ['enum', 'text', 'boolean'].includes(field?.type)
  const operators = operatorsForField(fieldKey).filter((operator) => operator.value !== 'in')

  const [customOpen, setCustomOpen] = useState(Boolean(customCondition) || !showFacets)

  // El borrador vive en local para que el operador se conserve mientras se
  // escribe el valor; solo las condiciones completas llegan al filtro real.
  const [customDraft, setCustomDraft] = useState(
    customCondition || {
      id: CUSTOM_ID(fieldKey),
      field: fieldKey,
      operator: operators[0]?.value || 'contains',
      value: '',
      value2: '',
    }
  )
  const customMeta = operatorMeta(fieldKey, customDraft.operator)

  /** Publica el grupo de la columna: faceta + condición personalizada activa. */
  const publish = ({ facet = facetCondition, custom = customDraft }) => {
    const next = [facet, custom].filter((item) => item && isConditionActive(item))
    onChange({ logic: 'AND', conditions: next })
  }

  const writeFacet = (values) => {
    publish({
      facet: values.length
        ? { id: FACET_ID(fieldKey), field: fieldKey, operator: 'in', value: values, value2: '' }
        : null,
    })
  }

  const writeCustom = (patch) => {
    const next = { ...customDraft, ...patch }
    setCustomDraft(next)
    publish({ custom: next })
  }

  const clearCustom = () => {
    const next = { ...customDraft, value: '', value2: '' }
    setCustomDraft(next)
    publish({ custom: null })
    setCustomOpen(showFacets ? false : true)
  }

  return (
    <Popover
      align="start"
      width={300}
      renderTrigger={({ open, toggle, props }) => (
        <button
          type="button"
          onClick={toggle}
          {...props}
          aria-label={`Opciones de la columna ${field?.label}`}
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
            activeCount > 0
              ? 'bg-primary text-primary-foreground'
              : open
                ? 'bg-accent text-primary'
                : 'text-muted-foreground opacity-0 group-hover/th:opacity-100 hover:bg-accent hover:text-primary focus-visible:opacity-100'
          )}
        >
          <FilterIcon size={13} />
        </button>
      )}
    >
      {({ close }) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[13px] font-semibold text-foreground">{field?.label}</p>
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {field?.type === 'number'
                ? 'numérico'
                : field?.type === 'date'
                  ? 'fecha'
                  : field?.type === 'boolean'
                    ? 'sí / no'
                    : field?.type === 'enum'
                      ? 'lista'
                      : 'texto'}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <Segmented
              ariaLabel="Ordenar columna"
              value={sort || ''}
              onChange={(value) => onSort(value === sort ? '' : value)}
              options={[
                { value: 'asc', label: 'Asc', icon: <ArrowUpIcon size={13} /> },
                { value: 'desc', label: 'Desc', icon: <ArrowDownIcon size={13} /> },
              ]}
            />
            {canHide && (
              <Button variant="ghost" size="sm" onClick={() => { onHide(); close() }}>
                <EyeIcon size={14} />
                Ocultar
              </Button>
            )}
          </div>

          {showFacets && (
            <div className="border-t border-border pt-2.5">
              <MultiSelectList
                options={facets}
                value={Array.isArray(facetCondition?.value) ? facetCondition.value : []}
                onChange={writeFacet}
                searchPlaceholder={`Buscar en ${field?.label.toLowerCase()}…`}
                listClassName={customOpen ? 'max-h-32' : undefined}
              />
            </div>
          )}

          <div className="border-t border-border pt-2.5">
            {!customOpen ? (
              <Button variant="ghost" size="sm" onClick={() => setCustomOpen(true)}>
                <FilterIcon size={14} />
                Condición personalizada
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    Condición
                  </span>
                  <IconButton label="Quitar condición" onClick={clearCustom}>
                    <XIcon size={14} />
                  </IconButton>
                </div>
                <Select
                  size="sm"
                  options={operators.map((operator) => ({
                    value: operator.value,
                    label: operator.label,
                  }))}
                  value={customDraft.operator}
                  onChange={(operator) => {
                    const nextMeta = operatorMeta(fieldKey, operator)
                    writeCustom(
                      nextMeta?.input === customMeta?.input
                        ? { operator }
                        : { operator, value: nextMeta?.input === 'multi' ? [] : '', value2: '' }
                    )
                  }}
                  ariaLabel="Operador"
                />
                {customMeta?.input !== 'none' && (
                  <ValueInput
                    condition={customDraft}
                    onChange={(next) =>
                      writeCustom({ value: next.value, value2: next.value2 })
                    }
                    options={facets.filter((facet) => facet.value !== '')}
                  />
                )}
              </div>
            )}
          </div>

          {activeCount > 0 && (
            <div className="flex justify-end border-t border-border pt-2.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange({ logic: 'AND', conditions: [] })}
              >
                Quitar filtros de la columna
              </Button>
            </div>
          )}
        </div>
      )}
    </Popover>
  )
}
